/**
 * Card Component - Controller
 * ============================
 *
 * Controlador del componente Card.
 * Maneja toda la lógica del componente:
 * - Inicialización y valores por defecto
 * - Generación de clases CSS
 * - Manejo de eventos
 * - Ciclo de vida del componente
 */

import type { IScope, ITranscludeFunction, IAugmentedJQuery } from 'angular';
import { hasTranscludedContent } from '../../_base/transclusion';
import {
  VARIANTS,
  SIZES,
  BASE_CLASSES,
  DEFAULTS,
  SPORT_PRIME_CHIP,
  SPORT_AVATAR,
  CardVariant,
  CardSize,
  CardLayout,
  VariantStyles,
  SizeStyles
} from './constants';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Interfaz para los bindings del componente
 */
export interface CardBindings {
  // Contenido
  title?: string;
  subtitle?: string;
  description?: string;
  caption?: string;
  badge?: string;
  imageUrl?: string;
  imageAlt?: string;
  avatar?: string;

  // Apariencia
  variant?: CardVariant;
  size?: CardSize;
  layout?: CardLayout;
  prime?: boolean;
  selected?: boolean;
  elevated?: boolean;
  disabled?: boolean;

  // Acciones
  onClick?: (args: { $event: Event }) => void;
  primaryAction?: (args: { $event: Event }) => void;
  primaryActionText?: string;
  secondaryAction?: (args: { $event: Event }) => void;
  secondaryActionText?: string;
}

/**
 * Interfaz para cambios en bindings
 */
interface BindingChanges {
  variant?: {
    currentValue: CardVariant;
    previousValue: CardVariant;
    isFirstChange: () => boolean;
  };
  size?: {
    currentValue: CardSize;
    previousValue: CardSize;
    isFirstChange: () => boolean;
  };
  [key: string]: unknown;
}

// ============================================================================
// CONTROLLER
// ============================================================================

/**
 * Clase controlador del Card
 *
 * BUENAS PRÁCTICAS:
 * - Usar static $inject para inyección de dependencias
 * - Implementar hooks de ciclo de vida ($onInit, $onChanges, $onDestroy)
 * - Mantener métodos pequeños y con una sola responsabilidad
 * - Documentar todos los métodos públicos
 */
export class CardController implements CardBindings {
  /**
   * Inyección de dependencias de Angular
   * Usar array notation garantiza que funcione con minificación
   */
  static $inject = ['$scope', '$element', '$transclude'];

  // Propiedades inyectadas
  private $scope: IScope | null;
  private $element: IAugmentedJQuery | null;
  private $transclude: ITranscludeFunction | null;

  // Bindings
  title?: string;
  subtitle?: string;
  description?: string;
  caption?: string;
  badge?: string;
  imageUrl?: string;
  imageAlt?: string;
  avatar?: string;
  variant?: CardVariant;
  size?: CardSize;
  layout?: CardLayout;
  prime?: boolean;
  selected?: boolean;
  elevated?: boolean;
  disabled?: boolean;
  onClick?: (args: { $event: Event }) => void;

  // Clases expuestas al template (variantes sport)
  primeChipClass: string = SPORT_PRIME_CHIP;
  avatarClass: string = SPORT_AVATAR;
  primaryAction?: (args: { $event: Event }) => void;
  primaryActionText?: string;
  secondaryAction?: (args: { $event: Event }) => void;
  secondaryActionText?: string;

  // Estado interno
  hasTransclude: boolean = false;

  // Estado compound: ¿algún hijo ecCardMedia se registró?
  compoundHasMedia: boolean = false;

  /**
   * Constructor del controlador
   */
  constructor(
    $scope: IScope,
    $element: IAugmentedJQuery,
    $transclude: ITranscludeFunction
  ) {
    this.$scope = $scope;
    this.$element = $element;
    this.$transclude = $transclude;

    // Bind de métodos para preservar contexto
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handlePrimaryAction = this.handlePrimaryAction.bind(this);
    this.handleSecondaryAction = this.handleSecondaryAction.bind(this);
  }

  // =========================================================================
  // LIFECYCLE HOOKS
  // =========================================================================

  /**
   * Inicialización del componente
   * Se ejecuta una vez que los bindings están disponibles
   */
  $onInit(): void {
    this._applyDefaults();
    this._checkTransclude();
  }

  /**
   * Detectar cambios en los bindings
   */
  $onChanges(changes: BindingChanges): void {
    if (changes.variant && !changes.variant.isFirstChange()) {
      this._validateVariant();
    }
    if (changes.size && !changes.size.isFirstChange()) {
      this._validateSize();
    }
  }

  /**
   * Limpieza al destruir el componente
   */
  $onDestroy(): void {
    // Limpiar referencias
    this.$scope = null;
    this.$element = null;
    this.$transclude = null;
  }

  // =========================================================================
  // MÉTODOS PRIVADOS
  // =========================================================================

  /**
   * Aplicar valores por defecto
   */
  private _applyDefaults(): void {
    this.variant = this.variant || DEFAULTS.variant;
    this.size = this.size || DEFAULTS.size;
    this.layout = this.layout || DEFAULTS.layout;
    this.elevated = this.elevated !== false ? DEFAULTS.elevated : false;
    this.disabled = this.disabled || DEFAULTS.disabled;
  }

  /**
   * Verificar si hay contenido transcluido.
   *
   * `isSlotFilled('')` NO sirve acá: con `transclude: true` booleano (como
   * este componente) $$slots solo se puebla en la forma multi-slot, así que
   * siempre devolvería false. Se usa `hasTranscludedContent`, que sondea el
   * contenido real con un clone descartable.
   */
  private _checkTransclude(): void {
    this.hasTransclude = hasTranscludedContent(this.$transclude);
  }

  /**
   * Validar que la variante sea válida
   */
  private _validateVariant(): void {
    if (this.variant && !VARIANTS[this.variant]) {
      console.warn(`[Card] Variant "${this.variant}" no válido. Usando "${DEFAULTS.variant}".`);
      this.variant = DEFAULTS.variant;
    }
  }

  /**
   * Validar que el tamaño sea válido
   */
  private _validateSize(): void {
    if (this.size && !SIZES[this.size]) {
      console.warn(`[Card] Size "${this.size}" no válido. Usando "${DEFAULTS.size}".`);
      this.size = DEFAULTS.size;
    }
  }

  // =========================================================================
  // MÉTODOS PÚBLICOS - CLASES CSS
  // =========================================================================

  /**
   * ¿El card usa un layout de deporte? (sport-row / sport-tile)
   */
  get isSport(): boolean {
    return this.layout === 'sport-row' || this.layout === 'sport-tile';
  }

  /**
   * ¿Hay contenido (fuera de la imagen) en el layout stack?
   * La imagen va siempre full-bleed; el bloque de contenido es el que lleva padding.
   */
  get hasStackContent(): boolean {
    return !!(
      this.title ||
      this.subtitle ||
      this.description ||
      this.caption ||
      this.hasTransclude ||
      this.showActions ||
      (this.badge && !this.imageUrl)
    );
  }

  /**
   * Obtener clases del contenedor principal
   * (la clase estática del template aporta ec-overflow-hidden; el radio, el
   *  cursor y la superficie se resuelven acá según el layout)
   */
  getContainerClasses(): string[] {
    if (this.isSport) {
      return this.getSportContainerClasses();
    }

    // Contenedor sin padding: la imagen va full-bleed y el bloque de contenido
    // aporta su propio padding (ver getStackContentClasses).
    const classes = [
      'ec-rounded-2xl',
      'ec-cursor-pointer',
      'ec-transition-all',
      'ec-duration-200',
      this.getVariantClasses().container
    ];

    if (this.elevated) {
      classes.push(BASE_CLASSES.elevated);
    }

    if (this.disabled) {
      classes.push(BASE_CLASSES.disabled);
    }

    return classes;
  }

  /**
   * Padding del bloque de contenido del stack.
   * Con imagen arriba: 12px de gap (pt) + 16px laterales/abajo.
   * Sin imagen: 16px en todo.
   */
  getStackContentClasses(): string {
    return this.imageUrl ? 'ec-pt-3 ec-px-4 ec-pb-4' : 'ec-p-4';
  }

  /**
   * Clases del contenedor para las variantes sport (Figma)
   * Superficie plana con sombra suave, radio 16px y padding 12/16.
   */
  getSportContainerClasses(): string[] {
    const classes = [
      'ec-bg-color-surface',
      'ec-rounded-2xl',
      'ec-shadow-card',
      'ec-pt-3',
      'ec-pb-4',
      'ec-px-4',
      'ec-transition-all',
      'ec-duration-200'
    ];

    if (this.onClick) {
      classes.push('ec-cursor-pointer');
    }

    if (this.selected) {
      classes.push('ec-border-2', 'ec-border-color-primary');
    }

    if (this.elevated) {
      classes.push('hover:ec-shadow-card-hover', 'hover:-ec-translate-y-0.5');
    }

    if (this.disabled) {
      classes.push(BASE_CLASSES.disabled);
    }

    return classes;
  }

  /**
   * Obtener clases para la variante actual
   */
  getVariantClasses(): VariantStyles {
    return VARIANTS[this.variant || DEFAULTS.variant] || VARIANTS[DEFAULTS.variant];
  }

  /**
   * Obtener clases para el tamaño actual
   */
  getSizeClasses(): SizeStyles {
    return SIZES[this.size || DEFAULTS.size] || SIZES[DEFAULTS.size];
  }

  /**
   * Obtener clases del título
   */
  getTitleClasses(): string[] {
    return [
      this.getVariantClasses().header,
      this.getSizeClasses().title
    ];
  }

  /**
   * Obtener clases del cuerpo
   */
  getBodyClasses(): string[] {
    const classes = [this.getSizeClasses().body];

    if (this.variant === 'prime') {
      classes.push('ec-text-white/80');
    } else {
      classes.push('ec-text-gray-600');
    }

    return classes;
  }

  /**
   * Obtener clases del footer
   */
  getFooterClasses(): string[] {
    return this.variant === 'prime' ? ['ec-border-white/20'] : ['ec-border-gray-100'];
  }

  /**
   * Obtener clases del botón primario
   */
  getPrimaryButtonClasses(): string[] {
    if (this.variant === 'prime') {
      return ['ec-bg-white', 'ec-text-prime', 'hover:ec-bg-white/90'];
    }
    return ['ec-bg-color-primary', 'ec-text-white', 'hover:ec-bg-color-primary-dark'];
  }

  /**
   * Obtener clases del botón secundario
   */
  getSecondaryButtonClasses(): string[] {
    if (this.variant === 'prime') {
      return ['ec-text-white', 'hover:ec-bg-white/10'];
    }
    return ['ec-text-gray-600', 'hover:ec-bg-gray-100'];
  }

  // =========================================================================
  // MÉTODOS PÚBLICOS - EVENTOS
  // =========================================================================

  /**
   * Manejar click en el card
   */
  handleClick($event: Event): void {
    if (this.disabled) {
      $event.preventDefault();
      $event.stopPropagation();
      return;
    }

    if (typeof this.onClick === 'function') {
      this.onClick({ $event });
    }
  }

  /**
   * Manejar teclado en el card (Enter / Espacio → click)
   */
  handleKeydown($event: KeyboardEvent): void {
    if (this.disabled) return;

    if ($event.key === 'Enter' || $event.key === ' ') {
      $event.preventDefault();
      this.handleClick($event);
    }
  }

  /**
   * Manejar acción primaria
   */
  handlePrimaryAction($event: Event): void {
    $event.stopPropagation();

    if (this.disabled) return;

    if (typeof this.primaryAction === 'function') {
      this.primaryAction({ $event });
    }
  }

  /**
   * Manejar acción secundaria
   */
  handleSecondaryAction($event: Event): void {
    $event.stopPropagation();

    if (this.disabled) return;

    if (typeof this.secondaryAction === 'function') {
      this.secondaryAction({ $event });
    }
  }

  // =========================================================================
  // GETTERS
  // =========================================================================

  /**
   * Verificar si mostrar el footer con acciones
   */
  get showActions(): boolean {
    return !!(this.primaryAction || this.secondaryAction);
  }

  /**
   * Modo compound: transclusión cruda en la raíz, sin bindings de contenido.
   * Solo aplica al layout 'stack' (default). Las variantes sport-row/sport-tile
   * no usan modo compound aunque tengan transclusión.
   */
  get isCompound(): boolean {
    return (!this.layout || this.layout === 'stack') &&
      this.hasTransclude && !this.title && !this.subtitle &&
      !this.description && !this.caption && !this.imageUrl && !this.showActions;
  }

  /**
   * Notificado por un hijo ecCardMedia en su $onInit.
   * Habilita el padding reducido (pt-3) del ecCardContent.
   */
  registerMedia(): void {
    this.compoundHasMedia = true;
  }
}

export default CardController;

// ============================================================================
// CONTROLLERS DE HIJOS (Compound Components)
// ============================================================================

/** Hijo compound: imagen full-bleed. Notifica al padre para el padding del content. */
export class CardMediaController {
  static $inject = [];
  // require ^?ecCard (opcional): el probe de `hasTranscludedContent` linkea un
  // clone DETACHED del árbol transcluido para sondear si hay contenido real.
  // En ese clone no existe un ecCard ancestro (está fuera del DOM real), así
  // que un require no-opcional lanzaría $compile:ctreq durante el probe. Con
  // '^?' el require resuelve a undefined ahí y este guard evita el crash.
  card?: CardController;
  src?: string;
  alt?: string;
  $onInit(): void {
    if (this.card) {
      this.card.registerMedia();
    }
  }
}

/** Hijo compound: bloque de contenido con padding según haya media. */
export class CardContentController {
  static $inject = [];
  // require ^?ecCard (opcional): mismo motivo que CardMediaController — el
  // probe de transclusión linkea un clone detached sin padre real.
  card?: CardController;
  getClasses(): string {
    return this.card && this.card.compoundHasMedia ? 'ec-pt-3 ec-px-4 ec-pb-4' : 'ec-p-4';
  }
}
